import React, { JSX } from "react";
import { useTranslations } from "next-intl";
import { parseNum } from "../constants";
import Field from "./Field";
import SectionTitle from "./SectionTitle";
import FilterDropdown from "./FilterDropdown";

const TradeModalExchangeForm = ({
  Icon,
  fieldErrors,
  selectedProduct,
  ex,
  setEx,
  giveMinQuantity,
  exchangeProducts,
  setExField,
  inp,
  CLS,
  setFieldErrors,
  giveProductPrice,
  allProducts,
  receiveProductPrice,
  extraPayableAmount,
}: {
  Icon: {
    box: JSX.Element;
    user: JSX.Element;
    payment: JSX.Element;
    arrowUp: JSX.Element;
    arrowDown: JSX.Element;
    arrowRight: JSX.Element;
    close: JSX.Element;
    whatsapp: JSX.Element;
  };
  fieldErrors: Record<string, string>;
  selectedProduct: { id: string; name: string; } | null | undefined;
  ex: {
    giveProduct: string;
    giveQuantity: string;
    receiveProduct: string;
    receiveQuantity: string;
    fullName: string;
    gst: string;
  };
  setEx: React.Dispatch<React.SetStateAction<typeof ex>>;
  giveMinQuantity: number;
  exchangeProducts: { id: string; name: string }[];
  setExField: (
    field: keyof typeof ex,
  ) => (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => void;
  inp: (error?: string) => string;
  CLS: {
    input: string;
  };
  setFieldErrors: React.Dispatch<React.SetStateAction<typeof fieldErrors>>;
  giveProductPrice: number;
  allProducts: { id: string; name: string; dontExchange?: boolean }[];
  receiveProductPrice: number;
  extraPayableAmount: number;
}) => {
  const t = useTranslations("tradeModal");
  return (
    <div className="space-y-3">
      <div>
        <SectionTitle icon={Icon.arrowUp}>
          {t("exchange.sections.productToGive")}
        </SectionTitle>
        <div className="grid grid-cols-2 gap-3 items-start">
          <Field
            label={t("exchange.fields.selectProduct")}
            required
            error={fieldErrors.giveProduct}
          >
            {selectedProduct ? (
              <input
                value={
                  selectedProduct.name.charAt(0).toUpperCase() +
                  selectedProduct.name.slice(1)
                }
                readOnly
                className={`${CLS.input} h-9.5 bg-slate-50 cursor-not-allowed text-slate-600`}
              />
            ) : (
              <FilterDropdown
                label=""
                value={ex.giveProduct}
                placeholder={t("exchange.placeholders.chooseProduct")}
                error={fieldErrors.giveProduct}
                options={[
                  {
                    value: "",
                    label: t("exchange.placeholders.chooseProduct"),
                  },
                  ...exchangeProducts
                    .filter((p) => p.id !== ex.receiveProduct)
                    .map((p) => ({
                      value: p.id,
                      label: p.name.charAt(0).toUpperCase() + p.name.slice(1),
                    })),
                ]}
                onChange={(value) =>
                  setExField("giveProduct")({
                    target: { value },
                  } as React.ChangeEvent<HTMLSelectElement>)
                }
              />
            )}
          </Field>
          <Field
            label={t("exchange.fields.giveQuantity")}
            required
            error={fieldErrors.giveQuantity}
          >
            <input
              type="number"
              min={giveMinQuantity}
              step={giveMinQuantity}
              value={ex.giveQuantity}
              onChange={(e) => {
                const raw = e.target.value;
                setEx((prev) => ({ ...prev, giveQuantity: raw }));
                const parsed = Number.parseInt(raw, 10);
                if (!raw || Number.isNaN(parsed)) {
                  setFieldErrors((prev) => ({ ...prev, giveQuantity: "" }));
                  return;
                }
                if (parsed <= 0) {
                  setFieldErrors((prev) => ({
                    ...prev,
                    giveQuantity: t("errors.quantityInvalid"),
                  }));
                } else if (
                  giveMinQuantity > 1 &&
                  parsed % giveMinQuantity !== 0
                ) {
                  setFieldErrors((prev) => ({
                    ...prev,
                    giveQuantity: t("errors.quantityMultiple", {
                      min: giveMinQuantity,
                      ex1: giveMinQuantity * 2,
                      ex2: giveMinQuantity * 3,
                    }),
                  }));
                } else {
                  setFieldErrors((prev) => ({ ...prev, giveQuantity: "" }));
                }
              }}
              onBlur={() => {
                const parsed = Number.parseInt(ex.giveQuantity, 10);
                if (!Number.isFinite(parsed) || parsed <= 0) {
                  setEx((prev) => ({
                    ...prev,
                    giveQuantity: String(giveMinQuantity),
                  }));
                  setFieldErrors((prev) => ({ ...prev, giveQuantity: "" }));
                  return;
                }
                if (giveMinQuantity > 1) {
                  const snapped = Math.max(
                    giveMinQuantity,
                    Math.round(parsed / giveMinQuantity) * giveMinQuantity,
                  );
                  setEx((prev) => ({ ...prev, giveQuantity: String(snapped) }));
                }
                setFieldErrors((prev) => ({ ...prev, giveQuantity: "" }));
              }}
              className={`${inp(fieldErrors.giveQuantity)} h-9.5`}
            />
          </Field>
        </div>
        {(ex.giveProduct || selectedProduct) && giveProductPrice > 0 && (
          <div className="mt-1.5 text-xs font-semibold text-green-700">
            {t("pricePerKg", {
              price: giveProductPrice.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
              }),
            })}
          </div>
        )}
      </div>
      <div className="mt-8">
        <SectionTitle icon={Icon.arrowDown}>
          {t("exchange.sections.productToReceive")}
        </SectionTitle>
        <div className="grid grid-cols-2 gap-3 items-start">
          <Field
            label={t("exchange.fields.selectProductToReceive")}
            required
            error={fieldErrors.receiveProduct}
          >
            <FilterDropdown
              label=""
              value={ex.receiveProduct}
              placeholder={t("exchange.placeholders.chooseProduct")}
              error={fieldErrors.receiveProduct}
              disabled={!ex.giveProduct}
              options={[
                {
                  value: "",
                  label: t("exchange.placeholders.chooseProduct"),
                },
                ...allProducts
                  .filter((p) => !p.dontExchange)
                  .map((p) => ({
                    value: p.id,
                    label:
                      p.name.charAt(0).toUpperCase() +
                      p.name.slice(1).toLowerCase(),
                    disabled: p.id === ex.giveProduct,
                  })),
              ]}
              onChange={(value) =>
                setExField("receiveProduct")({
                  target: { value },
                } as React.ChangeEvent<HTMLSelectElement>)
              }
            />
            {ex.receiveProduct && receiveProductPrice > 0 && (
              <div className="mt-1 text-xs font-semibold text-green-700">
                {t("pricePerKg", {
                  price: receiveProductPrice.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                  }),
                })}
              </div>
            )}
          </Field>
          <Field label={t("exchange.fields.receiveQuantity")}>
            <input
              type="number"
              value={ex.receiveQuantity}
              readOnly
              disabled
              className={`${CLS.input} h-9.5 bg-slate-200 cursor-not-allowed text-slate-500`}
            />
            {receiveProductPrice > 0 &&
              giveProductPrice > 0 &&
              parseNum(ex.giveQuantity) > 0 &&
              ex.receiveQuantity && (
                <p
                  className={`mt-1 text-xs font-semibold ${extraPayableAmount > 0 ? "text-blue-600" : "text-green-600"}`}
                >
                  {extraPayableAmount > 0
                    ? t("panel.exchange.extraPayable", {
                      amount: extraPayableAmount.toFixed(2),
                    })
                    : t("panel.exchange.exactMatch")}
                </p>
              )}
          </Field>
        </div>
      </div>
      <div className="mt-8">
        <SectionTitle icon={Icon.user}>
          {t("exchange.sections.yourInfo")}
        </SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label={t("exchange.fields.fullName")}
            required
            error={fieldErrors.fullName}
          >
            <input
              value={ex.fullName}
              onChange={setExField("fullName")}
              placeholder={t("exchange.placeholders.fullName")}
              className={inp(fieldErrors.fullName)}
            />
          </Field>
          <Field label={t("exchange.fields.gst")}>
            <input
              value={ex.gst}
              onChange={setExField("gst")}
              placeholder={t("exchange.placeholders.gst")}
              className={CLS.input}
            />
          </Field>
        </div>
      </div>
    </div>
  );
};

export default TradeModalExchangeForm;
